import { currentAccountId } from './account'

/**
 * WHO WORKS HERE, AND WHO IS AWAY.
 *
 * CREW-01, and deliberately about a third of it. The spec asks for a roster,
 * roles, skills, equipment, hours, time off, crew composition and an invite
 * flow with pending, expired and revoked states. Two of those are things this
 * product can hold honestly today. The rest are named on the screen instead of
 * being drawn.
 *
 * THERE IS NO INVITE, AND THAT IS NOT AN OMISSION. The spec's own safeguard is
 * that an invitation does not grant access until it is accepted and that crew
 * access is restricted server-side. This product has one login and no
 * server-side roles at all, so an Invite button would send nothing, grant
 * nothing and produce a row marked Pending forever. A person looking at that
 * row would reasonably believe their installer could see his own work.
 *
 * WHAT A ROSTER IS ACTUALLY FOR HERE, and it is worth more than the invite:
 * a visit can name who is going. That turns the schedule's conflict check from
 * an assumption into a fact. It has said "one crew is assumed, because there
 * is no crew record to assume otherwise" since the day it was written, and
 * this is the crew record.
 *
 * Stored beside the account like the letterhead and the shop defaults. It
 * never goes into a document, an export or a network request, and none of
 * these people can sign in, because there is nothing for them to sign in to.
 */

export interface Away {
  /** ISO yyyy-mm-dd, inclusive. */
  from: string
  /** ISO yyyy-mm-dd, inclusive. Equal to from for a single day. */
  to: string
  /** Free text. Vacation, jury duty, the reason nobody needs to justify. */
  why: string
}

export interface CrewMember {
  id: string
  name: string
  /** What they do, in the shop's own words. Lead, installer, helper. */
  role: string
  phone: string
  /** Days this person is not available. See awayOn. */
  away: Away[]
  /** Off the roster without losing the visits that already name them. */
  active: boolean
}

const keyFor = () => `lightsos.crew.${currentAccountId()}`

const plainDay = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10))

/**
 * A RANGE THAT RUNS BACKWARDS IS NOT A RANGE, and both ends are inclusive
 * because a person who says they are away Monday to Friday means Friday.
 */
export function validAway(a: Away): boolean {
  return plainDay(a.from) && plainDay(a.to) && a.from.slice(0, 10) <= a.to.slice(0, 10)
}

/** Compared as plain dates, never as instants. See lib/visits.ts on the trap. */
export function awayOn(m: CrewMember, day: string): Away | null {
  const d = day.slice(0, 10)
  for (const a of m.away) {
    if (!validAway(a)) continue
    if (d >= a.from.slice(0, 10) && d <= a.to.slice(0, 10)) return a
  }
  return null
}

export const nameOf = (roster: CrewMember[], id: string): string =>
  roster.find((m) => m.id === id)?.name.trim() || 'Somebody who is no longer on the roster'

/** The people a visit names, in roster order so a list of two reads the same twice. */
export function namesFor(roster: CrewMember[], ids: string[]): string[] {
  const set = new Set(ids)
  const out = roster.filter((m) => set.has(m.id)).map((m) => m.name.trim() || 'Unnamed')
  // Anybody named on a visit who is no longer on the roster still counts, or a
  // removed person silently empties every visit they were ever on.
  const known = new Set(roster.map((m) => m.id))
  for (const id of ids) if (!known.has(id)) out.push('Off the roster')
  return out
}

/** Said the way a person says it: Dan, Dan and Marcus, Dan and 2 others. */
export function crewWording(names: string[]): string {
  if (names.length === 0) return 'Nobody named'
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names[0]} and ${names.length - 1} others`
}

export function normalizeCrew(raw: unknown): CrewMember[] {
  if (!Array.isArray(raw)) return []
  const out: CrewMember[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const m = r as Partial<CrewMember>
    if (typeof m.id !== 'string' || !m.id) continue
    out.push({
      id: m.id,
      name: typeof m.name === 'string' ? m.name : '',
      role: typeof m.role === 'string' ? m.role : '',
      phone: typeof m.phone === 'string' ? m.phone : '',
      away: Array.isArray(m.away)
        ? (m.away as Away[])
            .filter((a) => a && typeof a === 'object' && validAway(a as Away))
            .map((a) => ({ from: a.from.slice(0, 10), to: a.to.slice(0, 10), why: String(a.why ?? '') }))
        : [],
      // Absent on anything saved before somebody could be taken off.
      active: m.active !== false,
    })
  }
  return out
}

export function loadCrew(): CrewMember[] {
  try {
    const raw = localStorage.getItem(keyFor())
    return raw ? normalizeCrew(JSON.parse(raw)) : []
  } catch {
    return []
  }
}

export function saveCrew(list: CrewMember[]) {
  try {
    localStorage.setItem(keyFor(), JSON.stringify(list))
  } catch {
    /* private mode: it just does not persist */
  }
}

export const newMember = (id: string): CrewMember => ({
  id,
  name: '',
  role: '',
  phone: '',
  away: [],
  active: true,
})

/**
 * The rest of CREW-01, named rather than drawn. Every line is a thing that
 * would need something this product does not have, and each says what.
 */
export const NOT_ON_THE_ROSTER: string[] = [
  'Invitations and logins. There is one login and no server-side roles, so an invite would send nothing and grant nothing, and a row marked Pending forever is worse than no row.',
  'Permissions. Same reason: with nothing to sign in to, a role grid decides nothing.',
  'Skills and equipment. There is no product or equipment record to match a person against.',
  'Hours and pay. Nothing here keeps a timesheet. Starting and finishing a stop on Today records when somebody was at a house, and that is all it is.',
]
